import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { warehouseOptionLabel } from '@/lib/warehouseLabel';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Save } from 'lucide-react';
import { cn } from '@/lib/utils';

type LocationType = 'HUB' | 'SPOKE' | 'VENUE';

interface ParentOption {
  id: string;
  name: string;
  code: string;
  type: 'HUB' | 'SPOKE';
}

interface FormData {
  type: LocationType;
  parentWarehouseId: string;
  code: string;
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postcode: string;
  country: string;
  monthlyOperatingCost: string;
  commissionPercent: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  isActive: boolean;
}

const initialFormData: FormData = {
  type: 'HUB',
  parentWarehouseId: '',
  code: '',
  name: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postcode: '',
  country: 'GB',
  monthlyOperatingCost: '0',
  commissionPercent: '0',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  isActive: true,
};

export default function LocationFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);
  const isVenueRoute = location.pathname.includes('/venues/');
  const initialType = (searchParams.get('type') as LocationType) || (isVenueRoute ? 'VENUE' : undefined);

  const [formData, setFormData] = useState<FormData>(() => {
    const base = { ...initialFormData };
    if (initialType === 'VENUE' || isVenueRoute) base.type = 'VENUE';
    return base;
  });
  const [hubs, setHubs] = useState<ParentOption[]>([]);
  const [allLocations, setAllLocations] = useState<ParentOption[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadHubs = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/warehouses?type=HUB');
      if (res.ok) {
        const data = await res.json();
        setHubs((data.items || []).map((w: ParentOption) => ({ ...w, type: 'HUB' as const })));
      }
    } catch {
      // ignore
    }
  }, []);

  const loadAllLocations = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/warehouses');
      if (res.ok) {
        const data = await res.json();
        setAllLocations(
          (data.items || []).map((w: ParentOption & { type?: string }) => ({
            id: w.id,
            name: w.name,
            code: w.code,
            type: (w.type || 'HUB') as 'HUB' | 'SPOKE',
          }))
        );
      }
    } catch {
      // ignore
    }
  }, []);

  const loadWarehouse = useCallback(async () => {
    if (!id || isVenueRoute) return;
    setLoading(true);
    try {
      const res = await apiFetch(`/api/v1/warehouses/${id}`);
      if (!res.ok) throw new Error('Failed to load location');
      const data = await res.json();
      setFormData((p) => ({
        ...p,
        type: data.type,
        parentWarehouseId: data.parentWarehouseId || '',
        code: data.code || '',
        name: data.name || '',
        addressLine1: data.addressLine1 || '',
        addressLine2: data.addressLine2 || '',
        city: data.city || '',
        postcode: data.postcode || '',
        country: data.country || 'GB',
        monthlyOperatingCost: String(data.monthlyOperatingCost ?? '0'),
        isActive: data.isActive ?? true,
      }));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load location');
    } finally {
      setLoading(false);
    }
  }, [id, isVenueRoute]);

  const loadVenue = useCallback(async () => {
    if (!id || !isVenueRoute) return;
    setLoading(true);
    try {
      const res = await apiFetch(`/api/v1/venues/${id}`);
      if (!res.ok) throw new Error('Failed to load venue');
      const data = await res.json();
      setFormData((p) => ({
        ...p,
        type: 'VENUE',
        parentWarehouseId: data.parentWarehouseId || '',
        name: data.name || '',
        addressLine1: data.addressLine1 || '',
        addressLine2: data.addressLine2 || '',
        city: data.city || '',
        postcode: data.postcode || '',
        country: data.country || 'GB',
        commissionPercent: String(data.commissionPercent ?? '0'),
        contactName: data.contactName || '',
        contactEmail: data.contactEmail || '',
        contactPhone: data.contactPhone || '',
        isActive: data.isActive ?? true,
      }));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load venue');
    } finally {
      setLoading(false);
    }
  }, [id, isVenueRoute]);

  useEffect(() => {
    loadHubs();
    if (formData.type === 'VENUE') loadAllLocations();
  }, [loadHubs, loadAllLocations, formData.type]);

  useEffect(() => {
    if (isVenueRoute) loadVenue();
    else loadWarehouse();
  }, [loadVenue, loadWarehouse, isVenueRoute]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (formData.type === 'SPOKE' && !formData.parentWarehouseId) {
      setError('Please select a parent hub for spoke locations.');
      return;
    }
    if (
      formData.type === 'VENUE' &&
      (!formData.addressLine1.trim() || !formData.city.trim() || !formData.postcode.trim())
    ) {
      setError('Address line 1, city, and postcode are required for venues.');
      return;
    }
    setSaving(true);
    try {
      if (formData.type === 'VENUE') {
        const venuePayload = {
          name: formData.name.trim(),
          address: {
            line1: formData.addressLine1.trim(),
            line2: formData.addressLine2.trim() || undefined,
            city: formData.city.trim(),
            postcode: formData.postcode.trim(),
            country: formData.country.trim() || 'GB',
          },
          parentWarehouseId: formData.parentWarehouseId || null,
          commissionPercent: parseFloat(formData.commissionPercent) || 0,
          contactName: formData.contactName.trim() || undefined,
          contactEmail: formData.contactEmail.trim() || undefined,
          contactPhone: formData.contactPhone.trim() || undefined,
          isActive: formData.isActive,
        };
        if (isEdit && id) {
          const res = await apiFetch(`/api/v1/venues/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(venuePayload),
          });
          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            throw new Error(errData.error || 'Failed to update venue');
          }
          navigate(`/locations/venues/${id}`);
        } else {
          const res = await apiFetch('/api/v1/venues', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(venuePayload),
          });
          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            throw new Error(errData.error || 'Failed to create venue');
          }
          await res.json();
          navigate('/locations');
        }
      } else {
        const warehousePayload = {
          type: formData.type,
          parentWarehouseId: formData.type === 'SPOKE' ? formData.parentWarehouseId || null : null,
          code: formData.code.trim(),
          name: formData.name.trim(),
          address:
            formData.addressLine1.trim() && formData.city.trim() && formData.postcode.trim()
              ? {
                  line1: formData.addressLine1.trim(),
                  line2: formData.addressLine2.trim() || undefined,
                  city: formData.city.trim(),
                  postcode: formData.postcode.trim(),
                  country: formData.country.trim() || 'GB',
                }
              : undefined,
          monthlyOperatingCost: parseFloat(formData.monthlyOperatingCost) || 0,
          isActive: formData.isActive,
        };
        if (isEdit && id) {
          const res = await apiFetch(`/api/v1/warehouses/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(warehousePayload),
          });
          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            throw new Error(errData.error || 'Failed to update location');
          }
          navigate(formData.type === 'HUB' ? `/locations/hubs/${id}` : `/locations/spokes/${id}`);
        } else {
          const res = await apiFetch('/api/v1/warehouses', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(warehousePayload),
          });
          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            throw new Error(errData.error || 'Failed to create location');
          }
          const created = await res.json();
          navigate(
            created.type === 'HUB' ? `/locations/hubs/${created.id}` : `/locations/spokes/${created.id}`
          );
        }
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  const pageTitle = isEdit ? 'Edit Location' : 'Add Location';
  const cardTitle = isEdit ? 'Edit Location' : 'New Location';
  const isVenue = formData.type === 'VENUE';

  return (
    <div className="space-y-6">
      <div className="mb-4 flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/locations')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">{pageTitle}</h1>
      </div>

      <Card className="w-full">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">{cardTitle}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="mx-auto max-w-5xl rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {error}
                </div>
              )}

              <div className="mx-auto grid w-full max-w-5xl grid-cols-1 gap-4 md:grid-cols-2 md:gap-x-8 md:gap-y-4">
                <div
                  className={cn(
                    'space-y-2',
                    isEdit && formData.type === 'HUB' && 'md:col-span-2 md:max-w-md'
                  )}
                >
                  <Label>Location type *</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(v: LocationType) =>
                      setFormData((p) => ({
                        ...p,
                        type: v,
                        parentWarehouseId: v === 'HUB' ? '' : p.parentWarehouseId,
                      }))
                    }
                    disabled={isEdit}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HUB">Hub</SelectItem>
                      <SelectItem value="SPOKE">Spoke</SelectItem>
                      <SelectItem value="VENUE">Venue</SelectItem>
                    </SelectContent>
                  </Select>
                  {isEdit && (
                    <p className="text-xs text-muted-foreground">
                      Location type cannot be changed after creation.
                    </p>
                  )}
                </div>

                {formData.type === 'SPOKE' && (
                  <div className="space-y-2">
                    <Label htmlFor="parentWarehouseId">Parent hub *</Label>
                    <Select
                      value={formData.parentWarehouseId}
                      onValueChange={(v) => setFormData((p) => ({ ...p, parentWarehouseId: v }))}
                    >
                      <SelectTrigger id="parentWarehouseId">
                        <SelectValue placeholder="Select a hub" />
                      </SelectTrigger>
                      <SelectContent>
                        {hubs.map((hub) => (
                          <SelectItem key={hub.id} value={hub.id}>
                            {warehouseOptionLabel(hub)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {formData.type === 'VENUE' && (
                  <div className="space-y-2">
                    <Label htmlFor="parentWarehouseIdVenue">Parent location (optional)</Label>
                    <Select
                      value={formData.parentWarehouseId || '__none__'}
                      onValueChange={(v) =>
                        setFormData((p) => ({ ...p, parentWarehouseId: v === '__none__' ? '' : v }))
                      }
                    >
                      <SelectTrigger id="parentWarehouseIdVenue">
                        <SelectValue placeholder="None (standalone venue)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">None (standalone venue)</SelectItem>
                        {allLocations.map((loc) => (
                          <SelectItem key={loc.id} value={loc.id}>
                            {warehouseOptionLabel(loc)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Venue can be under a hub or spoke, or standalone.
                    </p>
                  </div>
                )}

                {formData.type === 'HUB' && !isEdit && (
                  <div className="space-y-2">
                    <Label htmlFor="code">Code *</Label>
                    <Input
                      id="code"
                      value={formData.code}
                      onChange={(e) => setFormData((p) => ({ ...p, code: e.target.value }))}
                      placeholder="e.g. HUB-001 or SPOKE-001"
                      required
                      disabled={isEdit}
                    />
                  </div>
                )}

                {formData.type === 'SPOKE' && !isEdit && (
                  <div className="space-y-2 md:col-span-2 md:max-w-md">
                    <Label htmlFor="code">Code *</Label>
                    <Input
                      id="code"
                      value={formData.code}
                      onChange={(e) => setFormData((p) => ({ ...p, code: e.target.value }))}
                      placeholder="e.g. HUB-001 or SPOKE-001"
                      required
                    />
                  </div>
                )}

                {!isVenue && isEdit && (
                  <div className="space-y-2 md:col-span-2 md:max-w-md">
                    <Label htmlFor="code">Code *</Label>
                    <Input
                      id="code"
                      value={formData.code}
                      onChange={(e) => setFormData((p) => ({ ...p, code: e.target.value }))}
                      placeholder="e.g. HUB-001 or SPOKE-001"
                      required
                      disabled={isEdit}
                    />
                    <p className="text-xs text-muted-foreground">
                      Code cannot be changed after creation.
                    </p>
                  </div>
                )}

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                    placeholder="Location name"
                    required
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="addressLine1">Address line 1</Label>
                  <Input
                    id="addressLine1"
                    value={formData.addressLine1}
                    onChange={(e) => setFormData((p) => ({ ...p, addressLine1: e.target.value }))}
                    placeholder="Street address"
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="addressLine2">Address line 2</Label>
                  <Input
                    id="addressLine2"
                    value={formData.addressLine2}
                    onChange={(e) => setFormData((p) => ({ ...p, addressLine2: e.target.value }))}
                    placeholder="Apartment, suite, etc."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData((p) => ({ ...p, city: e.target.value }))}
                    placeholder="City"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postcode">Postcode</Label>
                  <Input
                    id="postcode"
                    value={formData.postcode}
                    onChange={(e) => setFormData((p) => ({ ...p, postcode: e.target.value }))}
                    placeholder="Postcode"
                  />
                </div>

                {!isVenue && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="country">Country</Label>
                      <Input
                        id="country"
                        value={formData.country}
                        onChange={(e) => setFormData((p) => ({ ...p, country: e.target.value }))}
                        placeholder="GB"
                        maxLength={2}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="monthlyOperatingCost">Monthly operating cost (£)</Label>
                      <Input
                        id="monthlyOperatingCost"
                        type="number"
                        min={0}
                        step={0.01}
                        value={formData.monthlyOperatingCost}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, monthlyOperatingCost: e.target.value }))
                        }
                        placeholder="0"
                      />
                    </div>
                  </>
                )}

                {isVenue && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="country">Country</Label>
                      <Input
                        id="country"
                        value={formData.country}
                        onChange={(e) => setFormData((p) => ({ ...p, country: e.target.value }))}
                        placeholder="GB"
                        maxLength={2}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="commissionPercent">Commission %</Label>
                      <Input
                        id="commissionPercent"
                        type="number"
                        min={0}
                        max={100}
                        step={0.01}
                        value={formData.commissionPercent}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, commissionPercent: e.target.value }))
                        }
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contactName">Contact name</Label>
                      <Input
                        id="contactName"
                        value={formData.contactName}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, contactName: e.target.value }))
                        }
                        placeholder="Contact name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contactEmail">Contact email</Label>
                      <Input
                        id="contactEmail"
                        type="email"
                        value={formData.contactEmail}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, contactEmail: e.target.value }))
                        }
                        placeholder="contact@example.com"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2 md:max-w-md">
                      <Label htmlFor="contactPhone">Contact phone</Label>
                      <Input
                        id="contactPhone"
                        value={formData.contactPhone}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, contactPhone: e.target.value }))
                        }
                        placeholder="Phone number"
                      />
                    </div>
                  </>
                )}

                <div className="flex items-center space-x-2 md:col-span-2">
                  <Checkbox
                    id="isActive"
                    checked={formData.isActive}
                    onCheckedChange={(checked) =>
                      setFormData((p) => ({ ...p, isActive: checked === true }))
                    }
                  />
                  <Label htmlFor="isActive">Active</Label>
                </div>

                <div className="flex flex-wrap gap-2 md:col-span-2">
                  <Button type="submit" disabled={saving}>
                    <Save className="mr-2 h-4 w-4" />
                    {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => navigate('/locations')}>
                    Cancel
                  </Button>
                </div>
              </div>
            </form>
        </CardContent>
      </Card>
    </div>
  );
}
